import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import usePageScrollLock from '../../../utils/usePageScrollLock';
import { useAdminShell } from './AdminShellContext';
import { ActionIcon } from './icons';

/**
 * Admin-panel primitives, built to the Portal handoff (design_handoff_admin_panel 3).
 * Every surface is white with no border and no shadow; controls are pills; the dark
 * cards are #0A0A0A with a 3px ring-gradient hairline along the top. JetBrains Mono
 * throughout (the app default).
 *
 * Absent, not disabled: nothing here renders a disabled control for a missing verb.
 */

/* ---------------------------------------------------------------- tokens */

export const RING = 'linear-gradient(90deg,#FD199F,#FF33C6,#B5B1FF,#127BFD,#22ECFB,#FAE1B4,#FCAA6D)';
export const RING_CONIC = 'conic-gradient(#FD199F,#FF33C6,#B5B1FF,#127BFD,#22ECFB,#FAE1B4,#FCAA6D,#FD199F)';
export const MAGENTA = '#FF00AA';
export const CYAN = '#00E5FF';

/** A black pill whose 2px border is the ring gradient (PULL EVENTS, PUBLISH, SAVE). */
export const ringBorder = (fill = '#000') => ({
  border: '2px solid transparent',
  background: `linear-gradient(${fill},${fill}) padding-box, ${RING} border-box`,
});

/** The 3px ring-gradient hairline that sits along the top of every dark card. */
export function RingLine() {
  return <span aria-hidden="true" className="pointer-events-none absolute left-0 right-0 top-0 h-[3px]" style={{ background: RING }} />;
}

/* ------------------------------------------------------------ formatting */

export const money = (cents) => `${cents < 0 ? '-' : ''}$${(Math.abs(cents || 0) / 100).toFixed(2)}`;
export const signedMoney = (cents) => `${cents >= 0 ? '+' : '-'}$${(Math.abs(cents || 0) / 100).toFixed(2)}`;
export const shortDate = (d) => {
  if (!d) return '—';
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '—';
  return `${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};
export const isoDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '—');
export const relTime = (d) => {
  if (!d) return 'never';
  const t = new Date(d).getTime();
  if (Number.isNaN(t)) return 'never';
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  if (s < 172800) return 'yesterday';
  if (s < 30 * 86400) return `${Math.floor(s / 86400)} days ago`;
  return isoDate(d);
};
export const hhmm = (d) => {
  const x = new Date(d);
  return `${String(x.getHours()).padStart(2, '0')}:${String(x.getMinutes()).padStart(2, '0')}`;
};
export const initialsOf = (name) => {
  if (!name) return '?';
  const local = name.includes('@') ? name.split('@')[0] : name;
  const parts = local.split(/[\s._-]+/).filter(Boolean);
  const s = parts.length >= 2 ? parts[0][0] + parts[1][0] : local.slice(0, 2);
  return s.toUpperCase();
};

/* --------------------------------------------------------------- buttons */

const btnBase = 'inline-flex items-center justify-center gap-2 rounded-full border-0 font-mono font-bold uppercase whitespace-nowrap transition-[background-color,color,opacity] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2';
const btnVariant = {
  plain: 'bg-[#F2F2F2] text-black hover:bg-[#E5E5E5]',
  solid: 'bg-black text-white hover:opacity-85',
  danger: 'bg-[#FEE2E2] text-[#DC2626] hover:bg-[#FECACA]',
  /** The destructive button in a modal, where it is the whole point. */
  red: 'bg-[#DC2626] text-white hover:bg-[#B91C1C]',
  /** On a dark card: the primary action. */
  white: 'bg-white text-black hover:opacity-85',
  /** On a dark card: everything else. */
  quiet: 'bg-[#1F1F1F] text-white hover:bg-[#2A2A2A]',
};
const btnSize = {
  page: 'h-11 px-5 text-[11px] tracking-[0.14em]',
  row: 'h-8 px-[14px] text-[9px] tracking-[0.12em]',
  card: 'h-10 px-4 text-[10px] tracking-[0.12em]',
  chip: 'h-[38px] px-4 text-[11px] tracking-[0.12em]',
  foot: 'h-11 flex-1 basis-auto px-4 text-[11px] tracking-[0.12em]',
  sheet: 'h-[46px] px-5 text-[11px] tracking-[0.14em]',
};

/** The one button. variant: plain | solid | danger | white | quiet. */
export function Btn({ variant = 'plain', size = 'page', ring = false, className = '', type = 'button', style, children, ...rest }) {
  return (
    <button
      type={type}
      className={`${btnBase} ${btnVariant[variant] || btnVariant.plain} ${btnSize[size] || btnSize.page} ${className}`}
      style={ring ? { ...ringBorder(variant === 'white' ? '#fff' : '#000'), ...style } : style}
      {...rest}
    >
      {children}
    </button>
  );
}

/** A filter chip. `selected` fills it black. */
export function Chip({ selected = false, className = '', children, ...rest }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      className={`inline-flex h-[38px] items-center gap-2 whitespace-nowrap rounded-full px-4 font-mono text-[11px] font-bold uppercase tracking-[0.12em] transition-colors duration-200 ${selected ? 'bg-black text-white' : 'bg-white text-black hover:bg-[#F2F2F2]'} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

/** A segmented pill: options sit inside one white track, the current one is black. */
export function SegTrack({ options, value, onChange, size = 'md', className = '', ariaLabel }) {
  const h = size === 'sm' ? 'h-8' : 'h-10';
  return (
    <div role="radiogroup" aria-label={ariaLabel} className={`pr-scroll-x flex max-w-full flex-shrink gap-[2px] rounded-full bg-white p-[3px] ${className}`}>
      {options.map((o) => {
        const on = String(o.value ?? '') === String(value ?? '');
        return (
          <button
            key={String(o.value ?? '')}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            className={`${h} whitespace-nowrap rounded-full px-[14px] font-mono text-[11px] font-bold uppercase tracking-[0.1em] transition-colors duration-200 ${on ? 'bg-black text-white' : 'bg-transparent text-black hover:bg-[#F2F2F2]'}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Close any popover when the pointer lands outside it or Escape is pressed. */
function useDismiss(open, close) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('touchstart', onDown); document.removeEventListener('keydown', onKey); };
  }, [open, close]);
  return ref;
}

/** "TYPE  ALL ▾" — a white pill that opens a dark rounded menu, magenta dot on the current option. */
export function DropChip({ label, value, onChange, options, className = '', align = 'left' }) {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  const current = options.find((o) => String(o.value ?? '') === String(value ?? '')) ?? options[0];
  const id = useId();
  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        className="flex h-[38px] items-center gap-2 whitespace-nowrap rounded-full bg-white px-[14px] pl-4 font-mono text-[11px] font-bold uppercase tracking-[0.1em] text-black hover:bg-[#F2F2F2]"
      >
        <span className="text-[#9CA3AF]">{label}</span>
        <span>{current?.label ?? 'ALL'}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d={open ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'} />
        </svg>
      </button>
      {open && (
        <div role="listbox" aria-labelledby={id} className={`absolute top-[calc(100%+8px)] z-50 max-h-[340px] min-w-[240px] max-w-[calc(100vw-24px)] overflow-y-auto rounded-[22px] bg-[#0A0A0A] p-2 ${align === 'right' ? 'right-0' : 'left-0'}`}>
          {options.map((o) => {
            const on = String(o.value ?? '') === String(value ?? '');
            return (
              <button
                key={String(o.value ?? '')}
                type="button"
                role="option"
                aria-selected={on}
                onClick={() => { onChange(o.value); setOpen(false); }}
                className={`flex min-h-[42px] w-full items-center justify-between gap-[10px] rounded-[14px] px-[14px] text-left font-mono text-[11px] font-bold uppercase tracking-[0.1em] text-white ${on ? 'bg-[#1F1F1F]' : 'bg-transparent'} hover:bg-[#1F1F1F]`}
              >
                {o.label}
                {on && <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ background: MAGENTA }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * The ⋯ popover — every action a row or a card has, behind one round button. On a phone
 * this is where a card's buttons live: six h44 pills under every row was the whole reason
 * the USERS page read as a wall of buttons. items: [{ label, onClick, variant, icon }] —
 * `danger` draws the label red, `icon` names an `ActionIcon` drawn before it (Oct 28 2026,
 * the USERS and HOSTS rows). The list is portalled into <body> at fixed coordinates taken
 * from the button, so a table's `overflow-x: auto` never clips it (Oct 29 2026); it opens
 * below, flips above when there is no room, stays on screen, and closes on scroll / resize.
 */
export function MoreMenu({ items = [], label = 'More actions', align = 'right', size = 32, tone = 'light', className = '', testId }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  // `{ divider: true }` separates groups; one at either end or two in a row is dropped, so a
  // caller can list every group and let the grants decide what survives.
  const acts = items.filter(Boolean).reduce((out, it) => (it.divider && (!out.length || out[out.length - 1].divider) ? out : [...out, it]), []);
  while (acts.length && acts[acts.length - 1].divider) acts.pop();

  // Place the popover once it has rendered (its height decides whether it flips up).
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    const btn = wrapRef.current?.getBoundingClientRect();
    const menu = menuRef.current;
    if (!btn || !menu) return;
    const gap = 6, edge = 12;
    const w = menu.offsetWidth, h = menu.offsetHeight;
    const vw = window.innerWidth, vh = window.innerHeight;
    const below = vh - btn.bottom - gap - edge;
    const top = h <= below || btn.top < h + gap + edge ? Math.min(btn.bottom + gap, Math.max(edge, vh - h - edge)) : btn.top - gap - h;
    let left = align === 'right' ? btn.right - w : btn.left;
    left = Math.max(edge, Math.min(left, vw - w - edge));
    setPos({ top: Math.max(edge, top), left });
  }, [open, align]);

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onDown = (e) => {
      if (wrapRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      close();
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    // A scroll inside the menu itself (a long list on a short screen) keeps it open.
    const onScroll = (e) => { if (!menuRef.current?.contains(e.target)) close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  if (acts.length === 0) return null;
  const menu = open && (
    <div
      ref={menuRef}
      role="menu"
      className="pr-overlay-root fixed z-[1000] min-w-[200px] max-w-[calc(100vw-24px)] overflow-y-auto rounded-[22px] bg-[#0A0A0A] p-2 shadow-[0_12px_32px_rgba(0,0,0,0.28)]"
      style={{ top: pos?.top ?? 0, left: pos?.left ?? 0, maxHeight: 'calc(100vh - 24px)', visibility: pos ? 'visible' : 'hidden' }}
      onClick={(e) => e.stopPropagation()}
    >
      {acts.map((it, i) => (it.divider ? <div key={`divider-${i}`} role="separator" className="mx-[10px] my-1 h-px bg-[#262626]" /> : (
        <button
          key={it.label}
          type="button"
          role="menuitem"
          disabled={it.disabled}
          onClick={() => { setOpen(false); it.onClick?.(); }}
          className={`flex min-h-[44px] w-full items-center gap-3 whitespace-nowrap rounded-[14px] px-[14px] text-left font-mono text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-[#1F1F1F] disabled:opacity-40 ${it.variant === 'danger' || it.variant === 'red' ? 'text-[#FF6B6B]' : it.variant === 'magenta' ? 'text-[#FF5FC8]' : 'text-white'}`}
          data-testid={it.testId}
        >
          {it.icon && <ActionIcon name={it.icon} size={16} />}
          {it.label}
        </button>
      )))}
    </div>
  );
  return (
    <div ref={wrapRef} className={`relative inline-flex flex-shrink-0 ${className}`} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid={testId}
        className={`flex items-center justify-center rounded-full font-mono text-[16px] font-bold leading-none transition-colors duration-200 ${tone === 'dark' ? 'bg-[#1F1F1F] text-white hover:bg-[#2A2A2A]' : open ? 'bg-black text-white' : 'bg-[#F2F2F2] text-black hover:bg-black hover:text-white'}`}
        style={{ width: size, height: size }}
      >
        ⋯
      </button>
      {menu && createPortal(menu, document.body)}
    </div>
  );
}

/** A search field in the filter row: a white pill with a magnifier. */
export function SearchField({ value, onChange, placeholder = 'SEARCH', onSubmit, className = '' }) {
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit?.(); }} className={`flex h-[38px] max-w-[360px] flex-1 basis-[220px] items-center gap-[10px] rounded-full bg-white px-4 ${className}`}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true" className="flex-shrink-0"><circle cx="11" cy="11" r="7" /><path d="M21 21l-5-5" /></svg>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="min-w-0 flex-1 border-0 bg-transparent p-0 font-mono text-[11px] uppercase tracking-[0.1em] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-0"
      />
    </form>
  );
}

export function DateChip({ label, value, onChange }) {
  return (
    <label className="inline-flex h-[38px] items-center gap-2 whitespace-nowrap rounded-full bg-white px-4 font-mono text-[11px] font-bold uppercase tracking-[0.1em]">
      <span className="text-[#9CA3AF]">{label}</span>
      <input type="date" value={value || ''} onChange={(e) => onChange(e.target.value)} className="border-0 bg-transparent p-0 font-mono text-[11px] focus:outline-none focus:ring-0" aria-label={label} />
    </label>
  );
}

/* --------------------------------------------------------------- surfaces */

/** The white card. Radius 24, no border, no shadow. */
export function Card({ title, right, children, bodyClass = 'px-5 pb-5', className = '', headerClass = '' }) {
  return (
    <section className={`min-w-0 rounded-[24px] bg-white ${title || right ? 'pt-5' : 'py-5'} ${className}`}>
      {(title || right) && (
        <header className={`flex flex-wrap items-baseline justify-between gap-[10px] px-5 pb-[14px] ${headerClass}`}>
          <span className="whitespace-nowrap font-mono text-[12px] font-bold uppercase tracking-[0.16em]">{title}</span>
          {right && <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.1em] text-[#9CA3AF]">{right}</div>}
        </header>
      )}
      <div className={bodyClass}>{children}</div>
    </section>
  );
}

/** A quiet note strip under a header — the sentence that explains the section. */
export function NoteCard({ title, children, tone = 'quiet', className = '' }) {
  const bg = tone === 'warn' ? '#FEF3C7' : tone === 'bad' ? '#FEE2E2' : '#FFFFFF';
  const color = tone === 'quiet' ? '#4B5563' : '#000';
  return (
    <div className={`flex flex-col gap-1 rounded-[20px] px-5 py-4 ${className}`} style={{ background: bg }}>
      {title && <span className="font-mono text-[11px] font-extrabold uppercase tracking-[0.14em] text-black">{title}</span>}
      <span className="font-mono text-[12px] leading-[1.6]" style={{ color }}>{children}</span>
    </div>
  );
}

/** The dark card: hero, event detail, drawer header, locked states. */
export function DarkCard({ className = '', children, radius = 28, style }) {
  return (
    <div className={`relative overflow-hidden bg-[#0A0A0A] text-white ${className}`} style={{ borderRadius: radius, ...style }}>
      <RingLine />
      {children}
    </div>
  );
}

/** Status pill. tone: neutral | good | bad | warn | info | magenta | dark. */
const PILL_TONES = {
  neutral: ['#F2F2F2', '#4B5563'],
  good: ['#DCFCE7', '#15803D'],
  bad: ['#FEE2E2', '#B91C1C'],
  warn: ['#FEF3C7', '#92400E'],
  info: ['#DBEAFE', '#1D4ED8'],
  magenta: ['#FFE3F4', '#BE0077'],
  dark: ['#000000', '#FFFFFF'],
};
export function Pill({ tone = 'neutral', className = '', children, ...rest }) {
  const [bg, fg] = PILL_TONES[tone] || PILL_TONES.neutral;
  return (
    <span className={`inline-flex items-center whitespace-nowrap rounded-full px-[11px] py-[5px] font-mono text-[9px] font-bold uppercase tracking-[0.12em] ${className}`} style={{ background: bg, color: fg }} {...rest}>
      {children}
    </span>
  );
}

export function FieldLabel({ children, className = '' }) {
  return <div className={`pl-4 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-[#9CA3AF] ${className}`}>{children}</div>;
}

export function Field({ label, error, children, className = '' }) {
  return (
    <div className={`flex min-w-0 flex-col gap-[6px] ${className}`}>
      {label && <FieldLabel>{label}</FieldLabel>}
      {children}
      {error && <div className="pl-4 font-mono text-[11px] text-[#DC2626]">{error}</div>}
    </div>
  );
}

/** h50 pill input on #F5F5F5 that shows a black border on focus. */
export const inputClass = 'w-full rounded-full border-2 border-transparent bg-[#F5F5F5] px-[18px] py-0 h-[50px] font-mono text-[13px] placeholder:text-[#9CA3AF] focus:border-black focus:bg-white focus:outline-none focus:ring-0';
/** The same, for a textarea (radius 18, not a pill). */
export const textareaClass = 'w-full rounded-[18px] border-2 border-transparent bg-[#F5F5F5] px-[18px] py-[14px] font-mono text-[13px] leading-[1.6] placeholder:text-[#9CA3AF] focus:border-black focus:bg-white focus:outline-none focus:ring-0';

export function ReadOnlyLine() {
  return <div className="font-mono text-[11px] uppercase tracking-[0.1em] text-[#BDBDBD]">YOU CAN VIEW THIS SECTION BUT NOT CHANGE IT.</div>;
}

/** The empty state inside a card. */
export function EmptyLine({ children = 'NOTHING HERE YET.', className = '' }) {
  return <div className={`rounded-[20px] bg-[#F7F7F7] px-4 py-10 text-center font-mono text-[12px] font-bold uppercase tracking-[0.14em] text-[#6B7280] ${className}`}>{children}</div>;
}

export function Loading({ className = '' }) {
  return <div className={`font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-[#6B7280] ${className}`}>LOADING…</div>;
}

/* ------------------------------------------------------------ stat tiles */

/** The sparkline under a tile: pink bars, the newest one cyan. */
export function TileSpark({ values = [] }) {
  const max = Math.max(1, ...values);
  return (
    <div className="mt-[2px] flex h-[26px] items-end gap-[3px]" aria-hidden="true">
      {values.map((v, i) => (
        <span key={i} className="flex-1 rounded-[3px]" style={{ height: `${Math.max(8, (v / max) * 100)}%`, background: i === values.length - 1 ? CYAN : MAGENTA }} />
      ))}
    </div>
  );
}

/**
 * Stat tile: a 10px label over a 26px value, with an optional delta pill, sub line,
 * sparkline, and a tooltip (rendered by the caller through `tip`). A grey 6px dot marks
 * the tiles that carry one.
 */
export function StatTile({ label, value, sub, delta, spark, tip, onClick, valueClass = '', small = false, className = '' }) {
  const [open, setOpen] = useState(false);
  const { isMobile } = useAdminShell();
  const deltaNeg = typeof delta === 'string' && delta.trim().startsWith('-');
  return (
    <div
      className={`relative flex min-w-0 flex-col gap-2 rounded-[22px] bg-white transition-colors duration-200 hover:bg-[#FCFCFC] ${isMobile ? 'p-[14px]' : 'p-[18px]'} ${onClick ? 'cursor-pointer' : ''} ${open ? 'z-50' : ''} ${className}`}
      onMouseEnter={tip ? () => setOpen(true) : undefined}
      onMouseLeave={tip ? () => setOpen(false) : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter') onClick(e); } : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-[#6B7280]">{label}</span>
        {delta != null && delta !== '' && (
          <span className="whitespace-nowrap rounded-full px-2 py-[3px] font-mono text-[9px] font-bold tracking-[0.06em]" style={deltaNeg ? { background: '#FEE2E2', color: '#B91C1C' } : { background: '#DCFCE7', color: '#15803D' }}>{delta}</span>
        )}
      </div>
      <span className={`truncate font-mono font-extrabold leading-[1.1] ${small ? 'text-[20px]' : isMobile ? 'text-[22px]' : 'text-[26px]'} ${valueClass}`}>{value ?? '—'}</span>
      {sub && <span className="font-mono text-[10px] tracking-[0.06em] text-[#6B7280]">{sub}</span>}
      {spark && spark.length > 0 && <TileSpark values={spark} />}
      {tip && <span aria-hidden="true" className="absolute bottom-[14px] right-4 h-[6px] w-[6px] rounded-full bg-[#E5E7EB]" />}
      {tip && open && <div className="absolute left-0 top-[calc(100%+8px)] z-[60] w-full min-w-[240px] cursor-default text-left">{tip}</div>}
    </div>
  );
}

/** A grid of tiles. `min` is the column floor. */
export function TileGrid({ label, min = '200px', className = '', children }) {
  const { isMobile } = useAdminShell();
  // Two across on a phone: `auto-fill` at the column floor gives one tall column there,
  // and a wall of single tiles reads worse than the pair.
  const cols = isMobile ? 'repeat(2, minmax(0, 1fr))' : `repeat(auto-fill, minmax(${min}, 1fr))`;
  return (
    <div className={`flex flex-col gap-[10px] ${className}`}>
      {label && <span className="px-1 pt-[6px] font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-[#6B7280]">{label}</span>}
      <div className={isMobile ? 'grid gap-2' : 'grid gap-[10px]'} style={{ gridTemplateColumns: cols }}>{children}</div>
    </div>
  );
}

/* ---------------------------------------------------------------- people */

/** A round photo (or initials) — people are always round, 36px in a table row. */
export function Avatar({ url, initials, name, size = 36, className = '' }) {
  return (
    <span
      title={name}
      className={`inline-flex flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#F2F2F2] font-mono text-[9px] font-bold uppercase text-[#4B5563] ${className}`}
      style={{ width: size, height: size }}
    >
      {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : (initials || initialsOf(name))}
    </span>
  );
}

/** An event / poster thumbnail — radius 14, never round. */
export function Thumb({ url, size = 48, radius = 14, className = '' }) {
  return (
    <span className={`inline-block flex-shrink-0 overflow-hidden bg-[#F2F2F2] ${className}`} style={{ width: size, height: size, borderRadius: radius }}>
      {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : null}
    </span>
  );
}

/** Initials in a round pill — kept for the rail footer and the staff rows. */
export function InitialsBox({ initials, name, size = 30, className = '' }) {
  return <Avatar initials={initials} name={name} size={size} className={className} />;
}

/** The capability-grid cell: a 28px radius-9 box. */
export function CheckBox({ checked, onChange, label, size = 28, disabled = false }) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-[10px] font-mono text-[11px] font-bold uppercase tracking-[0.1em]">
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        onClick={() => !disabled && onChange(!checked)}
        className={`flex flex-shrink-0 items-center justify-center rounded-[9px] transition-colors duration-200 ${checked ? 'bg-black text-white' : 'bg-[#F2F2F2] text-transparent'} ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
        style={{ width: size, height: size }}
      >
        <GridCheck size={Math.max(10, size - 14)} />
      </button>
      {label}
    </label>
  );
}

/** The capability-grid check: round caps, currentColor. */
export function GridCheck({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 7.5L5.5 11L12 3.5" />
    </svg>
  );
}

/** A white radius-28 card — confirms, invites, the two access states. */
export function HardCard({ children, className = '' }) {
  return <div className={`relative overflow-hidden rounded-[28px] bg-white ${className}`}><RingLine />{children}</div>;
}

/** Sub-tab row: pills inside one white track. */
export function SubTabs({ tabs, active, onChange, note, actions, className = '' }) {
  const { isMobile } = useAdminShell();
  return (
    <div className={`flex min-w-0 max-w-full flex-wrap items-center gap-2 ${isMobile ? 'w-full' : ''} ${className}`}>
      <div className={`pr-scroll-x flex min-w-0 max-w-full gap-[2px] rounded-full bg-white p-[3px] ${isMobile ? 'w-full' : ''}`}>
        {tabs.map((t) => {
          const on = active === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => onChange(t.key)}
              aria-pressed={on}
              className={`flex h-9 items-center gap-2 whitespace-nowrap rounded-full px-4 font-mono text-[11px] font-bold uppercase tracking-[0.12em] transition-colors duration-200 ${on ? 'bg-black text-white' : 'text-black hover:bg-[#F2F2F2]'}`}
            >
              {t.label}
              {t.count != null && (
                <span className={`flex h-5 min-w-[20px] items-center justify-center rounded-full px-[6px] font-mono text-[9px] ${on ? 'bg-white text-black' : 'bg-[#F2F2F2] text-[#6B7280]'}`}>{t.count}</span>
              )}
            </button>
          );
        })}
      </div>
      {note && <span className="flex-1 basis-[200px] px-[6px] font-mono text-[11px] text-[#6B7280]">{note}</span>}
      {actions}
    </div>
  );
}

/** The filter row: chips, segments, drops and a search, all on one line. */
export function FilterRow({ className = '', children }) {
  return <div className={`relative z-[15] flex flex-wrap items-center gap-[6px] ${className}`}>{children}</div>;
}

/* --------------------------------------------------------------- tooltip */

/**
 * The one tooltip — shared by tiles, bars, chart columns, y-axis labels and x ticks.
 * Dark, radius 20, ring-gradient hairline, an optional breakdown with pink tracks, the
 * query it came from, and a drill button.
 */
export function AdminTooltip({ title, kicker, desc, rows, breakdownLabel, breakdown, source, drill, onDrill, className = '', style }) {
  const max = Math.max(1, ...(breakdown || []).map((b) => b.value || 0));
  return (
    <div className={`relative flex flex-col gap-[10px] overflow-hidden rounded-[20px] bg-[#0A0A0A] px-4 pb-[14px] pt-4 text-white ${className}`} style={style}>
      <RingLine />
      <div className="flex items-baseline justify-between gap-[10px]">
        <span className="font-mono text-[11px] font-extrabold uppercase tracking-[0.14em]">{title}</span>
        {kicker && <span className="whitespace-nowrap font-mono text-[10px] text-[#8A8A8A]">{kicker}</span>}
      </div>
      {desc && <span className="font-mono text-[11px] leading-[1.55] text-[#BDBDBD]">{desc}</span>}
      {rows && rows.length > 0 && (
        <div className="flex flex-col gap-[6px]">
          {rows.map((r) => (
            <div key={r.label} className="flex items-center gap-2 font-mono text-[11px]">
              <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ background: r.color || MAGENTA }} />
              <span className="min-w-0 flex-1 truncate tracking-[0.06em] text-[#BDBDBD]">{r.label}</span>
              <span className="font-extrabold text-[13px]">{r.value}</span>
            </div>
          ))}
        </div>
      )}
      {breakdown && breakdown.length > 0 && (
        <div className={`flex flex-col gap-2 ${rows && rows.length ? 'border-t border-[#262626] pt-[10px]' : ''}`}>
          {breakdownLabel && <span className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-[#8A8A8A]">{breakdownLabel}</span>}
          {breakdown.slice(0, 5).map((b) => (
            <div key={b.label} className="flex flex-col gap-1">
              <div className="flex justify-between gap-2 font-mono text-[10px]">
                <span className="min-w-0 truncate text-[#E5E5E5]">{b.label}</span>
                <span className="font-extrabold">{b.display ?? b.value}</span>
              </div>
              <div className="h-[5px] rounded-full bg-[#262626]"><div className="h-full rounded-full" style={{ width: `${Math.round(((b.value || 0) / max) * 100)}%`, background: MAGENTA }} /></div>
            </div>
          ))}
        </div>
      )}
      {source && <span className="font-mono text-[9px] leading-[1.5] text-[#737373]">{source}</span>}
      {drill && onDrill && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onDrill(); }} className="h-9 rounded-full bg-white font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-black hover:opacity-85">{drill}</button>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- detail */

/**
 * The detail card — an event, a gathering, a run. A dark radius-28 card with the
 * subject's photo behind it at 38% under a left-to-right gradient, a status pill, the
 * title, the action pills, an optional funding bar, and the info pills.
 *
 * info: [{ label, value, onClick? }] — a clickable pill is underlined.
 */
export function DetailCard({ image, statusLabel, statusTone = 'neutral', kicker, title, sub, actions, progress, info = [] }) {
  const [bg, fg] = PILL_TONES[statusTone] || PILL_TONES.neutral;
  return (
    <div className="relative overflow-hidden rounded-[28px] bg-[#0A0A0A] text-white">
      {image && <img src={image} alt="" className="absolute inset-0 block h-full w-full object-cover opacity-[.38]" />}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(10,10,10,.96) 0%, rgba(10,10,10,.78) 55%, rgba(10,10,10,.35) 100%)' }} />
      <RingLine />
      <div className="relative flex flex-col gap-[18px] p-6">
        <div className="flex flex-wrap items-start justify-between gap-[14px]">
          <div className="flex min-w-0 flex-1 basis-[300px] flex-col gap-[10px]">
            <span className="flex flex-wrap items-center gap-2">
              {statusLabel && <span className="rounded-full px-[10px] py-1 font-mono text-[9px] font-bold uppercase tracking-[0.14em]" style={{ background: bg, color: fg }}>{statusLabel}</span>}
              {kicker && <span className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#9CA3AF]">{kicker}</span>}
            </span>
            <span className="font-mono text-[26px] font-bold leading-[1.12] tracking-[-0.01em]">{title}</span>
            {sub && <span className="font-mono text-[11px] leading-[1.6] tracking-[0.1em] text-[#BDBDBD]">{sub}</span>}
          </div>
          {actions && <div className="flex flex-wrap gap-[6px]">{actions}</div>}
        </div>
        {progress && (
          <div className="flex max-w-[560px] flex-col gap-2">
            <div className="flex justify-between gap-[10px] font-mono text-[11px] font-bold uppercase tracking-[0.1em]"><span>{progress.left}</span><span className="text-[#9CA3AF]">{progress.right}</span></div>
            <div className="h-[10px] overflow-hidden rounded-full bg-[#262626]">
              <div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, progress.percent))}%`, background: 'linear-gradient(90deg,#FD199F,#FF33C6,#B5B1FF,#127BFD,#22ECFB)' }} />
            </div>
          </div>
        )}
        {info.length > 0 && (
          <div className="flex flex-wrap gap-[6px]">
            {info.map((i) => (
              <span
                key={i.label}
                onClick={i.onClick}
                role={i.onClick ? 'button' : undefined}
                tabIndex={i.onClick ? 0 : undefined}
                onKeyDown={i.onClick ? (e) => { if (e.key === 'Enter') i.onClick(); } : undefined}
                className={`flex flex-col gap-[3px] rounded-[18px] px-4 py-[10px] ${i.onClick ? 'cursor-pointer hover:bg-white/[.14]' : ''}`}
                style={{ background: 'rgba(255,255,255,.08)' }}
              >
                <span className="font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-[#9CA3AF]">{i.label}</span>
                <span className={`font-mono text-[12px] font-bold ${i.onClick ? 'underline decoration-[#6B7280] underline-offset-[3px]' : ''}`}>{i.value}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- toast */

/** The black pill toast at the bottom centre. */
export function Toast({ message }) {
  if (!message) return null;
  return (
    <div role="status" className="pointer-events-none fixed bottom-6 left-1/2 z-[100] flex max-w-[calc(100%-32px)] -translate-x-1/2 items-center gap-[10px] rounded-full bg-[#0A0A0A] py-3 pl-[14px] pr-5 font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-white">
      <span className="h-[10px] w-[10px] flex-shrink-0 rounded-full" style={{ background: RING }} />
      {message}
    </div>
  );
}

/* ----------------------------------------------------------------- modal */

/**
 * The panel's modal: a white radius-28 card with the ring-gradient hairline, on a dark
 * scrim. Escape and a click on the scrim close it. `footer` holds the two pill buttons.
 *
 * This is the panel's own modal on purpose — the app-wide `Modal` is the brutalist one
 * every other surface uses, and restyling it would move every dialog in the product. The
 * app-wide `showConfirm` grows an `admin` theme instead (see DialogContext), so a confirm
 * is still one promise everywhere.
 */
export function AdminModal({ open, onClose, title, kicker, children, footer, width = 520, labelledBy }) {
  usePageScrollLock(open);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 safe-inset z-[120] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,.55)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={labelledBy ? undefined : title}
        aria-labelledby={labelledBy}
        className="relative flex max-h-[86vh] w-full flex-col overflow-hidden rounded-[28px] bg-white"
        style={{ maxWidth: width }}
      >
        <RingLine />
        <header className="flex items-start justify-between gap-3 px-6 pb-3 pt-6">
          <div className="flex min-w-0 flex-col gap-1">
            <span className="font-mono text-[14px] font-bold uppercase tracking-[0.14em]">{title}</span>
            {kicker && <span className="font-mono text-[11px] leading-[1.5] text-[#6B7280]">{kicker}</span>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#F2F2F2] font-mono text-[14px] font-bold hover:bg-black hover:text-white"
          >
            ✕
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-2">{children}</div>
        {footer && <footer className="flex flex-wrap justify-end gap-2 px-6 pb-6 pt-4">{footer}</footer>}
      </div>
    </div>
  );
}

/**
 * The rows behind a number: a photo (round for a person, radius-6 for anything else), a
 * label, an optional second line, and the value in a pill. A row with `href` or `onClick`
 * is a button — this is how a chart drills into the records it was drawn from.
 */
export function DrillList({ items = [], empty = 'NOTHING BEHIND THIS ONE.', onPick }) {
  if (items.length === 0) return <EmptyLine>{empty}</EmptyLine>;
  return (
    <ul className="m-0 flex list-none flex-col gap-1 p-0">
      {items.map((it, i) => {
        const pick = it.onClick || (onPick && (it.href || it.id) ? () => onPick(it) : undefined);
        const Row = pick ? 'button' : 'div';
        return (
          <li key={it.key ?? `${it.label}-${i}`}>
            <Row
              type={pick ? 'button' : undefined}
              onClick={pick}
              className={`flex w-full items-center gap-3 rounded-[18px] px-3 py-[10px] text-left transition-colors duration-200 ${pick ? 'cursor-pointer hover:bg-[#F7F7F7]' : ''}`}
            >
              {it.image !== undefined && (it.round === false
                ? <Thumb url={it.image} size={32} radius={10} />
                : <Avatar url={it.image} name={it.label} size={32} />)}
              <span className="flex min-w-0 flex-1 flex-col gap-[2px]">
                <span className="truncate font-mono text-[12px] font-bold uppercase tracking-[0.06em]">{it.label}</span>
                {it.sub && <span className="truncate font-mono text-[10px] text-[#6B7280]">{it.sub}</span>}
              </span>
              {it.display != null && (
                <span className="flex-shrink-0 rounded-full bg-[#F2F2F2] px-3 py-[5px] font-mono text-[11px] font-extrabold">{it.display}</span>
              )}
            </Row>
          </li>
        );
      })}
    </ul>
  );
}
